import { Reveal, MaskedInView } from "../Reveal";
import { ArrowRight, FileText, Stethoscope, Salad, CalendarDays, BookOpen } from "lucide-react";
import { scrollToForm } from "../SiteNav";
import FemipalImage from "../../assets/FemiImage.png";

const STAGES = [
  { name: "Periods", note: "Cycles that stop being a mystery" },
  { name: "Emotions", note: "Know why you are feeling so" },
  { name: "Curiosities", note: "New information about an unknown change in your body" },
  { name: "PCOS", note: "Hormonal health, finally decoded" },
  { name: "Menopause", note: "A new chapter, fully supported" },
];

const STEPS = ["Track", "Understand", "Ask/Consult", "Manage", "Follow up"];

const FEATURES = [
  { icon: FileText, title: "Reports", desc: "Upload lab reports, track, understand and get right advice" },
  { icon: Stethoscope, title: "Doctors", desc: "Consult gynecologists who specialise in hormonal health." },
  { icon: Salad, title: "Diet", desc: "Nutrition built around you, not trends." },
  { icon: CalendarDays, title: "Cycle Tracking", desc: "Periods, symptoms, moods — one calm timeline." },
  { icon: BookOpen, title: "Education", desc: "Knowledge and answers you want & right recommendations" },
];

function ChapterHead({ num, eyebrow, title, testId }) {
  return (
    <div data-testid={testId} className="mb-6 md:mb-10">
      <Reveal>
        <p className="text-sm md:text-[16px] uppercase tracking-[0.1em] font-bold text-[#F3698F] mb-2 md:mb-3">
          {num} — {eyebrow}
        </p>
      </Reveal>
      <MaskedInView>
        <h2 className="font-serif-display font-light tracking-tight leading-[1.1] md:leading-[1.05] text-3xl sm:text-4xl md:text-6xl max-w-3xl">
          {title}
        </h2>
      </MaskedInView>
    </div>
  );
}

export default function Chapters() {
  return (
    <div className="overflow-hidden">
      {/* 01 — Why Femipal */}
      <section className="py-10 md:py-28" data-testid="chapter-why">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 md:px-10 grid grid-cols-12 gap-8 md:gap-10 items-center">
          <div className="col-span-12 lg:col-span-7">
            <ChapterHead
              num="01"
              eyebrow="Why Femipal"
              title={<>Women's healthcare shouldn't feel like a <em className="italic text-[#F3698F]">puzzle.</em></>}
              testId="chapter-why-head"
            />
            <Reveal delay={0.1} className="max-w-xl">
              <p className="text-sm sm:text-base md:text-lg text-[#D1C6DF] leading-relaxed">
                A symptom here, a report there, a different doctor every visit. Women piece together
                their own hormonal health for years — often decades — without a single place that
                sees the whole picture.
              </p>
              <p className="text-sm sm:text-base md:text-lg text-[#F7F3FA] leading-relaxed mt-4 md:mt-6">
                Femipal is being built to be that place. One companion that understands your stage,
                your history, and your next step.
              </p>
            </Reveal>
          </div>
          <div className="col-span-12 lg:col-span-5 mt-2 lg:mt-0">
            <Reveal delay={0.2}>
              <div className="relative overflow-hidden rounded-sm border hairline aspect-[4/5] max-w-md mx-auto lg:max-w-none">
                <img
                  src={FemipalImage}
                  alt="Confident woman in dim light"
                  data-testid="chapter-why-image"
                  className="w-full h-full object-cover grayscale hover:grayscale-0 transition-[filter] duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#17151B]/50 to-transparent" />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* 02 — Who is Femipal for */}
      <section className="py-10 md:py-28 border-t hairline" data-testid="chapter-who">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 md:px-10">
          <ChapterHead
            num="02"
            eyebrow="Who is Femipal for"
            title={<>Every stage. <em className="italic text-[#D1C6DF]">Every her.</em></>}
            testId="chapter-who-head"
          />
          <div>
            {STAGES.map((stage, i) => (
              <Reveal key={stage.name} delay={i * 0.06}>
                <div
                  data-testid={`stage-row-${i}`}
                  className="group flex flex-col md:grid md:grid-cols-12 md:items-baseline gap-2 md:gap-4 py-5 md:py-9 border-t hairline last:border-b hover:bg-[#282037] transition-colors duration-500 cursor-default px-3 md:px-4"
                >
                  <div className="flex items-baseline gap-4 md:contents">
                    <span className="overline-label text-[#8F82A2] text-xs md:text-sm md:col-span-1">
                      0{i + 1}
                    </span>
                    <h3 className="font-serif-display font-light text-2xl sm:text-3xl md:text-5xl tracking-tight group-hover:text-[#F3698F] md:group-hover:translate-x-2 transition-[color,transform] duration-500 md:col-span-5">
                      {stage.name}
                    </h3>
                  </div>
                  <p className="text-xs sm:text-sm md:text-base text-[#D1C6DF] md:text-right md:col-span-6 pl-8 md:pl-0">
                    {stage.note}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 03 — How it works */}
      <section className="py-10 md:py-28 border-t hairline" data-testid="chapter-how">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 md:px-10">
          <ChapterHead
            num="03"
            eyebrow="How it works"
            title={<>A rhythm, not a <em className="italic text-[#D1C6DF]">one-off visit.</em></>}
            testId="chapter-how-head"
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-px bg-[#3B3051] border hairline rounded-sm overflow-hidden">
            {STEPS.map((step, i) => (
              <Reveal key={step} delay={i * 0.08} className="bg-[#17151B]">
                <div
                  data-testid={`how-step-${i}`}
                  className="p-6 md:p-10 h-full flex flex-col justify-between min-h-[160px] md:min-h-[220px] group hover:bg-[#282037] transition-colors duration-500"
                >
                  <span className="font-serif-display italic text-xl md:text-2xl text-[#F3698F]">
                    0{i + 1}
                  </span>
                  <div>
                    <h3 className="font-serif-display font-light text-xl sm:text-2xl md:text-3xl tracking-tight mt-6 md:mt-8">
                      {step}
                    </h3>
                    <div className="w-8 h-px bg-[#F3698F] mt-3 md:mt-4 group-hover:w-full transition-[width] duration-700" />
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 04 — What you can do */}
      <section className="py-10 md:py-28 border-t hairline" data-testid="chapter-what">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 md:px-10">
          <ChapterHead
            num="04"
            eyebrow="What you can do"
            title={<>Your whole hormonal life, <em className="italic text-[#D1C6DF]">in one place.</em></>}
            testId="chapter-what-head"
          />
          <div className="grid grid-cols-1 md:grid-cols-6 gap-3 md:gap-4">
            {FEATURES.map((f, i) => (
              <Reveal
                key={f.title}
                delay={i * 0.07}
                className={i < 2 ? "md:col-span-3" : "md:col-span-2"}
              >
                <div
                  data-testid={`feature-card-${i}`}
                  className="group border hairline rounded-sm p-6 sm:p-8 md:p-10 h-full bg-[#1E1C22] hover:bg-[#282037] hover:border-[#F3698F]/40 transition-[background-color,border-color] duration-500"
                >
                  <f.icon className="w-5 h-5 md:w-6 md:h-6 text-[#F3698F] mb-6 md:mb-10 group-hover:scale-110 transition-transform duration-500" strokeWidth={1.5} />
                  <h3 className="font-serif-display font-light text-xl sm:text-2xl md:text-3xl tracking-tight mb-2 md:mb-3">
                    {f.title}
                  </h3>
                  <p className="text-xs sm:text-sm md:text-base text-[#D1C6DF] leading-relaxed">
                    {f.desc}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

export function FoundingCta() {
  return (
    <section className="py-10 md:py-28 border-t hairline" data-testid="chapter-founding">
      <div className="max-w-7xl mx-auto px-5 sm:px-6 md:px-10">
        <Reveal>
          <p className="text-sm md:text-[16px] uppercase tracking-[0.1em] font-bold text-[#F3698F] mb-2 md:mb-3">
            05 — Founding 100
          </p>
        </Reveal>
        <MaskedInView>
          <h2 className="font-serif-display font-light tracking-tight leading-[1.1] md:leading-[1.08] text-3xl sm:text-4xl md:text-6xl max-w-4xl">
            "Help us build Femipal <em className="italic text-[#F3698F]">with</em> women, not just for women."
          </h2>
        </MaskedInView>
        <Reveal delay={0.15}>
          <p className="text-sm sm:text-base md:text-lg text-[#D1C6DF] leading-relaxed max-w-xl mt-6 md:mt-8">
            The first hundred members shape what Femipal becomes — testing the app before launch,
            telling us what's broken, and deciding what gets built next.
          </p>
          <button
            onClick={scrollToForm}
            data-testid="founding-join-button"
            className="group mt-8 md:mt-10 overline-label border border-[#F3698F] text-[#F3698F] px-6 md:px-8 py-3.5 md:py-4 rounded-full hover:bg-[#F3698F] hover:text-white transition-colors duration-300 flex items-center justify-center gap-3 w-full sm:w-auto"
          >
            Join Founding 100
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
          </button>
        </Reveal>
      </div>
    </section>
  );
}